import type { HttpDataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { fetchDataSource, interpolateHttpRequest } from '../fetchDataSource';
import { offeringsRequiredBodyFields } from '../offeringsRequiredBodyFields';
import { omitEmptyFormStateQueryKeys } from '../omitEmptyFormStateQuery';
import type { DataSourceTypeDescriptor } from '../source-type-registry';

/**
 * Author-supplied URL, method, headers, query, and body — the original source
 * type, and the one a declaration with no `kind` at all resolves to.
 */
export const httpSourceType: DataSourceTypeDescriptor<HttpDataSourceDeclaration> =
  {
    kind: 'http',

    buildRequest(decl, context) {
      return interpolateHttpRequest(decl, context);
    },

    fetch(request, decl, options) {
      return fetchDataSource(decl, undefined, {
        ...options,
        preparedRequest: request,
      });
    },

    isSupported(decl) {
      return (
        typeof decl.endpoint === 'string' &&
        (decl.method === 'GET' || decl.method === 'POST')
      );
    },

    interpolatableFields(decl) {
      return [decl.endpoint, decl.headers, decl.query, decl.body];
    },

    /**
     * URL-shaping fields only. An unresolved token in the endpoint or a
     * header turns into an invalid request target or lookup key. Empty
     * `formState` tokens in query are omitted first, so an optional facet
     * does not defer the fetch; a sibling-source token in query still does.
     * An unresolved optional body criterion does not defer, and many POST
     * sources rely on JSON serialization dropping one.
     */
    dependencyFields(decl, context) {
      const query =
        decl.query === undefined
          ? undefined
          : omitEmptyFormStateQueryKeys(decl.query, context);
      return [decl.endpoint, decl.headers, query];
    },

    requiredResolvedFields(decl) {
      return offeringsRequiredBodyFields(decl);
    },
  };
